import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { useEffect, useState } from "react";

/**
 * Generic version of `@react-hook/switch`'s `useSwitch` for any value type.
 *
 * Supports both controlled and uncontrolled modes:
 * - Uncontrolled: `useControlledValue(defaultValue)`
 * - Controlled: `useControlledValue(defaultValue, controlledValue)`
 * - Controlled with callback: `useControlledValue(defaultValue, controlledValue, onChange)`
 *
 * When `controlledValue` is provided, the internal state syncs to it.
 * Unlike MUI's `useControlled`, `setValue` always updates internal state,
 * allowing temporary divergence (e.g., empty input during editing).
 */
export function useControlledValue<T>(
  defaultValue: T,
  controlledValue?: T,
  onChange?: (value: T) => void,
): [T, (value: T) => void] {
  const [current, setCurrent] = useState(controlledValue ?? defaultValue);

  useEffect(() => {
    if (controlledValue !== undefined) {
      setCurrent(controlledValue);
    }
  }, [controlledValue]);

  const setValue = useHandler((value: T) => {
    setCurrent(value);
    onChange?.(value);
  });

  return [current, setValue];
}
